import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import stile from './GeminiChat.module.css';
import { sendMessage } from '../services/gemini';
import { useSprache } from '../sprache';

// react-markdown (samt remark/unified) erst laden, wenn der Chat tatsaechlich Nachrichten zeigt --
// haelt es aus dem Haupt-Bundle; bis dahin steht der Rohtext da
const ReactMarkdown = lazy(() => import('react-markdown'));

// Chat-Punkte-Icon
const ChatPunkteIcon = ({ groesse = 18, klasse }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={groesse} height={groesse} fill="currentColor" className={klasse} viewBox="0 0 16 16">
    <path d="M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
    <path d="m2.165 15.803.02-.004c1.83-.363 2.948-.842 3.468-1.105A9.06 9.06 0 0 0 8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6a10.437 10.437 0 0 1-.524 2.318l-.003.011a10.722 10.722 0 0 1-.244.637c-.079.186.074.394.273.362a21.673 21.673 0 0 0 .693-.125zm.8-3.108a1 1 0 0 0-.287-.801C1.618 10.83 1 9.468 1 8c0-3.192 3.004-6 7-6s7 2.808 7 6c0 3.193-3.004 6-7 6a8.06 8.06 0 0 1-2.088-.272 1 1 0 0 0-.711.074c-.387.196-1.24.57-2.634.893a10.97 10.97 0 0 0 .398-2z" />
  </svg>
);

// Schließen-Icon
const SchliessenIcon = ({ groesse = 18, klasse }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={groesse} height={groesse} fill="currentColor" className={klasse} viewBox="0 0 16 16">
    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
  </svg>
);

// Senden-Icon
const SendenIcon = ({ groesse = 18, klasse }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={groesse} height={groesse} fill="currentColor" className={klasse} viewBox="0 0 16 16">
    <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z" />
  </svg>
);

const GeminiChat = () => {
  // Zustandsverwaltung fuer den Chat
  const { sprache, t } = useSprache();
  const [istGeoeffnet, setIstGeoeffnet] = useState(false);
  const [zeigeTooltip, setZeigeTooltip] = useState(true);
  // Begruessung und Fehlermeldung als Textschluessel: folgen so einem spaeteren Sprachwechsel
  const [nachrichten, setNachrichten] = useState([{ schluessel: 'chat.begruessung', istBot: true }]);
  const [eingabeText, setEingabeText] = useState('');
  const [laedtAntwort, setLaedtAntwort] = useState(false);
  const nachrichtenEndeReferenz = useRef(null);

  const nachUntenScrollen = () => {
    nachrichtenEndeReferenz.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    nachUntenScrollen();
  }, [nachrichten, istGeoeffnet]);

  // Nachricht an die Gemini-Schnittstelle absenden
  const nachrichtAbsenden = async () => {
    if (!eingabeText.trim()) return;

    const benutzerNachricht = eingabeText.trim();
    setEingabeText('');
    setNachrichten((vorherige) => [...vorherige, { text: benutzerNachricht, istBot: false }]);
    setLaedtAntwort(true);

    try {
      const antwortText = await sendMessage(benutzerNachricht, sprache);
      setNachrichten((vorherige) => [...vorherige, { text: antwortText, istBot: true }]);
    } catch (fehler) {
      setNachrichten((vorherige) => [
        ...vorherige,
        { schluessel: 'chat.verbindungsfehler', istBot: true },
      ]);
    } finally {
      setLaedtAntwort(false);
    }
  };

  const chatOeffnen = () => {
    setIstGeoeffnet(true);
    setZeigeTooltip(false);
  };

  return (
    <div className={stile.chatContainer}>
      {/* Geoeffnetes Chat-Fenster */}
      {istGeoeffnet && (
        <div className={stile.chatWindow} role="region" aria-label={t('chat.fenster')}>
          <div className={stile.header}>
            <div className={stile.headerTitle}>
              <span className={stile.onlineStatusPunkt} title="Online" />
              <ChatPunkteIcon groesse={18} />
              <span>{t('chat.titel')}</span>
            </div>
            <button
              className={stile.closeButton}
              onClick={() => setIstGeoeffnet(false)}
              aria-label={t('chat.schliessen')}
            >
              <SchliessenIcon groesse={20} />
            </button>
          </div>

          <div className={stile.messages}>
            {nachrichten.map((nachricht, index) => (
              <div
                key={index}
                className={`${stile.message} ${nachricht.istBot ? stile.botMessage : stile.userMessage}`}
              >
                <Suspense fallback={<p>{nachricht.schluessel ? t(nachricht.schluessel) : nachricht.text}</p>}>
                  <ReactMarkdown>{nachricht.schluessel ? t(nachricht.schluessel) : nachricht.text}</ReactMarkdown>
                </Suspense>
              </div>
            ))}

            {/* Animierter Pastell-Ladeindikator */}
            {laedtAntwort && (
              <div className={`${stile.message} ${stile.botMessage} ${stile.tippContainer}`}>
                <div className={stile.typingDot} />
                <div className={stile.typingDot} />
                <div className={stile.typingDot} />
              </div>
            )}
            <div ref={nachrichtenEndeReferenz} />
          </div>

          <form
            className={stile.inputArea}
            onSubmit={(ereignis) => {
              ereignis.preventDefault();
              nachrichtAbsenden();
            }}
            autoComplete="off"
          >
            <input
              type="text"
              className={stile.input}
              placeholder={t('chat.platzhalter')}
              value={eingabeText}
              onChange={(e) => setEingabeText(e.target.value)}
              disabled={laedtAntwort}
              autoComplete="off"
              aria-label={t('chat.eingabe')}
            />
            <button
              type="submit"
              className={stile.sendButton}
              disabled={laedtAntwort || !eingabeText.trim()}
              aria-label={t('chat.senden')}
            >
              <SendenIcon groesse={17} />
            </button>
          </form>
        </div>
      )}

      {/* Schwebender Chat-Button mit Tooltip */}
      {!istGeoeffnet && (
        <div className={stile.chatButtonWrapper}>
          {zeigeTooltip && (
            <div className={stile.chatTooltip} onClick={chatOeffnen}>
              <button
                type="button"
                className={stile.tooltipCloseBtn}
                onClick={(ereignis) => {
                  ereignis.stopPropagation();
                  setZeigeTooltip(false);
                }}
                aria-label={t('chat.hinweisAusblenden')}
              >
                <SchliessenIcon groesse={14} />
              </button>
              <div className={stile.tooltipContent}>
                <span>{t('chat.tooltipTitel')}</span>
                <p>{t('chat.tooltipVor')}<strong>{t('chat.tooltipZwilling')}</strong>{t('chat.tooltipNach')}</p>
              </div>
            </div>
          )}
          <button
            className={stile.chatButton}
            onClick={chatOeffnen}
            aria-label={t('chat.oeffnen')}
            title={t('chat.oeffnenHinweis')}
          >
            <ChatPunkteIcon groesse={26} />
          </button>
        </div>
      )}
    </div>
  );
};

export default GeminiChat;
